import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWallet } from "@aptos-labs/wallet-adapter-react";
import { useQuery } from "@tanstack/react-query";
import { BkfcConnectDrawer } from "../bkfc/components/BkfcConnectDrawer";
import { BkfcBackButton } from "../bkfc/components/BkfcBackButton";
import { useOwnedCertificates } from "../bkfc/useOwnedCertificates";
import {
  fetchCertificateStatuses,
  sendRaffleLink,
  BkfcCertificateStatus,
} from "../bkfc/api";

const BKFC_SHOP_URL = "https://www.bkfc.com";

// "My certificates" (Figma 3693:541, the final design): certificate gallery +
// per-chip raffle actions, a "My rewards" section for raffle wins, and the
// BKFC merchandise banner. Ownership comes from the Aptos indexer (like the
// reference app's gallery); the backend adds the off-chain raffle state.
interface CertCard {
  uid: string;
  raffleDrawn: boolean;
  raffleWon: boolean | null;
  rewardName: string | null;
}

const truncateUid = (uid: string) =>
  uid.length > 20 ? `${uid.slice(0, 19)}...` : uid;

const formatToday = () =>
  new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const BkfcCertificates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { connected, account, isLoading: walletLoading } = useWallet();
  const address = account?.address?.toString();

  const [connectOpen, setConnectOpen] = useState(false);
  const [linkNotices, setLinkNotices] = useState<Record<string, string>>({});
  const [sendingUid, setSendingUid] = useState<string | null>(null);

  const {
    certificates: owned,
    isLoading: ownedLoading,
    error: ownedError,
  } = useOwnedCertificates(address);

  const { data: statuses, refetch: refetchStatuses } = useQuery({
    queryKey: ["bkfc-certificate-statuses", address],
    enabled: !!address,
    queryFn: async () => {
      const res = await fetchCertificateStatuses(address!);
      return res.certificates;
    },
  });

  // Union of indexer ownership and backend records, keyed by chip UID. The
  // indexer is the ownership truth; backend rows fill in raffle state and
  // cover certificates the indexer hasn't caught up on yet.
  const cards: CertCard[] = useMemo(() => {
    const byUid = new Map<string, CertCard>();
    (statuses ?? []).forEach((s: BkfcCertificateStatus) => {
      byUid.set(s.uid, {
        uid: s.uid,
        raffleDrawn: s.raffleDrawn,
        raffleWon: s.raffleWon,
        rewardName: s.rewardName,
      });
    });
    (owned ?? []).forEach((cert) => {
      if (!byUid.has(cert.uid)) {
        byUid.set(cert.uid, {
          uid: cert.uid,
          raffleDrawn: false,
          raffleWon: null,
          rewardName: null,
        });
      }
    });
    return Array.from(byUid.values());
  }, [owned, statuses]);

  const rewards = useMemo(
    () => cards.filter((c) => c.raffleDrawn && c.raffleWon && c.rewardName),
    [cards],
  );

  const handleSendLink = async (uid: string) => {
    if (sendingUid) return;
    setSendingUid(uid);
    try {
      const res = await sendRaffleLink(uid);
      setLinkNotices((prev) => ({
        ...prev,
        [uid]: res.success
          ? "Link sent — check your email"
          : res.cooldownSeconds
            ? `Sent recently — retry in ${res.cooldownSeconds}s`
            : res.message,
      }));
      refetchStatuses();
    } catch (err) {
      setLinkNotices((prev) => ({
        ...prev,
        [uid]: err instanceof Error ? err.message : "Failed to send link",
      }));
    } finally {
      setSendingUid(null);
    }
  };

  return (
    <div className="px-6 pt-8 pb-6">
      {/* location.key is "default" only for the first in-app location, i.e.
          the page was opened directly (emailed link) and back would leave
          the site — only show Back when there is in-app history */}
      {location.key !== "default" && (
        <BkfcBackButton onClick={() => navigate(-1)} className="mb-4" />
      )}

      {/* My 223 Portal bar */}
      <div className="w-full border border-black rounded-[14px] p-3.5 flex items-center justify-between">
        <span className="text-base font-bold text-black">My 223 Portal</span>
        <span className="flex items-center gap-1.5">
          <img src="/svg/bkfc/calendar.svg" alt="" className="w-4 h-4" />
          <span className="text-xs text-black">{formatToday()}</span>
        </span>
      </div>

      <h1 className="mt-5 text-[22px] font-bold text-black">My certificates</h1>

      {/* while autoConnect is restoring the previous session, show a quiet
          loading state instead of flashing the connect prompt */}
      {!connected && walletLoading && (
        <div className="mt-5 flex flex-col items-center gap-4 py-8 border border-[#B8B8B6] rounded-[18px]">
          <img
            src="/images/bkfc/loading-badge.png"
            alt="Loading"
            className="w-12 animate-pulse"
          />
          <p className="text-base text-[#626262] text-center px-6">
            Reconnecting your wallet...
          </p>
        </div>
      )}

      {!connected && !walletLoading && (
        <div className="mt-5 flex flex-col items-center gap-4 py-8 border border-[#B8B8B6] rounded-[18px]">
          <p className="text-base text-[#626262] text-center px-6">
            Connect your wallet to see the certificates you have claimed
          </p>
          <button
            onClick={() => setConnectOpen(true)}
            className="bg-[#FFD43E] h-10 px-10 rounded-[50px] text-black font-bold transition-colors hover:bg-[#f5c822]"
          >
            Connect wallet
          </button>
        </div>
      )}

      {connected && (
        <>
          {ownedLoading && !cards.length && (
            <p className="mt-5 text-base text-[#626262]">
              Loading your certificates...
            </p>
          )}

          {ownedError && !cards.length && (
            <p className="mt-5 text-base text-[#626262]">{ownedError}</p>
          )}

          {!ownedLoading && !cards.length && !ownedError && (
            <p className="mt-5 text-base text-[#626262]">
              No BKFC certificates in this wallet yet. Tap a BKFC toy to claim
              one!
            </p>
          )}

          {cards.length > 0 && (
            <div className="mt-5 grid grid-cols-2 gap-4">
              {cards.map((cert) => (
                <div
                  key={cert.uid}
                  className="border border-[#B8B8B6] rounded-[18px] pt-2.5 px-2.5 pb-4 flex flex-col items-center gap-2.5"
                >
                  <img
                    src="/images/bkfc/cert-tile.png"
                    alt="BKFC Certificate of Authenticity"
                    className="w-full h-[174px] object-cover rounded-[14px]"
                  />

                  <p className="text-[10px] text-[#626262] text-center w-full break-all">
                    {truncateUid(cert.uid)}
                  </p>

                  {cert.raffleDrawn ? (
                    <span className="h-[34px] px-3.5 rounded-[80px] bg-[#FAFAF8] border border-[#B8B8B6] text-xs font-semibold text-[#838383] flex items-center">
                      Raffle done
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSendLink(cert.uid)}
                      disabled={sendingUid === cert.uid}
                      className="h-[34px] px-3.5 rounded-[80px] bg-black text-xs font-semibold text-white flex items-center transition-colors hover:bg-gray-800 disabled:opacity-60"
                    >
                      {sendingUid === cert.uid ? "Sending..." : "Claim my Reward"}
                    </button>
                  )}

                  {linkNotices[cert.uid] && (
                    <p className="text-[10px] text-[#626262] text-center">
                      {linkNotices[cert.uid]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Raffle wins (Figma "My rewards" section) */}
          {rewards.length > 0 && (
            <>
              <h2 className="mt-5 text-[22px] font-bold text-black">
                My rewards
              </h2>
              <div className="mt-5 grid grid-cols-2 gap-4">
                {rewards.map((cert) => (
                  <div
                    key={cert.uid}
                    className="border border-[#B8B8B6] rounded-[18px] pt-2.5 px-2.5 pb-4 flex flex-col items-center gap-2.5"
                  >
                    <img
                      src="/images/bkfc/cert-tile.png"
                      alt={cert.rewardName ?? "BKFC reward"}
                      className="w-full h-[174px] object-cover rounded-[14px]"
                    />
                    <p className="text-[10px] text-[#626262] text-center w-full">
                      {cert.rewardName}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Official merchandise banner (Figma "Banner container / original fonts") */}
      <div className="relative mt-4 h-[326px] rounded-[14px] overflow-hidden">
        <img
          src="/images/bkfc/merch-banner-bg.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="relative h-full w-full bg-gradient-to-r from-[rgba(255,255,255,0.9)] to-[rgba(124,124,124,0.9)] flex flex-col items-center justify-between px-4 pt-8 pb-6">
          <div className="flex flex-col items-center gap-[18px] w-full text-center">
            <div className="font-oswald font-medium uppercase text-black">
              <h2 className="text-[30px] leading-normal tracking-[-0.3px]">
                Official BKFC merchandise
              </h2>

              <p className="mt-1.5 text-sm tracking-[-0.14px]">
                Check out the latest. New looks &amp; killer deals
              </p>
            </div>

            <button
              onClick={() =>
                window.open(BKFC_SHOP_URL, "_blank", "noopener,noreferrer")
              }
              className="bg-[#FFD43E] h-10 px-7 rounded-[60px] font-oswald font-medium text-xs uppercase text-black hover:opacity-90 transition-opacity"
            >
              Shop now
            </button>
          </div>

          <div className="w-full aspect-[356/120]">
            <img
              src="/images/bkfc/merch-products.png"
              alt="Official BKFC merchandise"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {connectOpen && (
        <BkfcConnectDrawer
          onClose={() => setConnectOpen(false)}
          onConnected={() => setConnectOpen(false)}
        />
      )}
    </div>
  );
};

export default BkfcCertificates;
